import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { 
  Syringe, 
  Drop, 
  Sparkle, 
  Eye,
  ArrowRight,
  ArrowLeft,
  Check,
  Phone,
  EnvelopeSimple,
  MapPin,
  InstagramLogo,
  FacebookLogo,
  LinkedinLogo,
  List,
  X as CloseIcon,
  Dna,
  Atom,
  Lightning,
  FileArrowDown
} from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Product Data for Individual Pages
const productData = {
  "jalupro-classic": {
    name: "Jalupro Classic",
    tagline: "Foundation for Skin Rejuvenation",
    description: "Jalupro Classic is an injectable dermal biorevitalization product consisting of a functional amino acid cluster at a concentration of 1% and free hyaluronic acid with a low molecular weight.",
    longDescription: `Jalupro Classic is an injectable dermal biorevitalization product consisting of a functional amino acid cluster at a concentration of 1% and free hyaluronic acid with a low molecular weight. Its formulation is enhanced by combining the unique amino acid cluster of amino acids such as Glycine, L-Proline, L-Leucine, L-Lysine in the right amount and ratio, combined with low molecular weight hyaluronic acid.

JALUPRO CLASSIC is the perfect choice for restoring the radiance and firmness of the skin.

As a rejuvenation therapy, it maintains high collagen production, balanced elastin, and good skin structure in young patients. It prevents and reduces future loss of elasticity in the face.

It offers an excellent option for skin reconstruction, especially in combination with other procedures such as energy-based devices. Combined with glycine's anti-inflammatory activity to help restore new collagen, Jalupro Classic helps reduce the appearance of scars (including acne), keloids, and red stretch marks.`,
    classicActions: [
      "Helps stimulate fibroblast activity",
      "Activate collagen and elastin synthesis",
      "Reduce the rate of collagen degradation",
      "Repair the structure of the dermis"
    ],
    classicNote: "Jalupro helps to improve the quality of the skin by supporting the activation of tissue regeneration, and shortens the rehabilitation period after aggressive procedures by increasing its density and elasticity.",
    benefits: [
      "Restores radiance and firmness of the skin",
      "Maintains high collagen production and balanced elastin",
      "Prevents and reduces future loss of elasticity",
      "Excellent for skin reconstruction with energy-based devices",
      "Reduces appearance of scars, keloids, and stretch marks",
      "Glycine's anti-inflammatory activity restores new collagen"
    ],
    idealFor: [
      "Photo-aging and signs of aging (moderate and severe) on face and body",
      "Stretch mark treatments (supportive)",
      "Before aesthetic procedures to support and increase effectiveness",
      "Combined with energy-based devices to extend duration of effect",
      "Jalutox protocol to reduce and prevent signs of aging",
      "Pre- and post-surgery skin preparation and quality improvement",
      "Supportive in hair follicle-stimulating protocols for alopecia"
    ],
    treatmentAreas: ["Face", "Neck", "Décolleté", "Under arms", "Hands", "Inside of legs", "Abdomen", "Knees", "Stretch mark areas"],
    sessions: "3-4 sessions recommended, 2-3 weeks apart",
    results: "Visible improvement after 2-3 sessions, lasting 4-6 months",
    image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/yv180hf7_image.png",
    beforeAfterCombined: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/s4hvu82q_DECOLTE%20copia.jpg",
    brochureUrl: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/18ggfyqw_03_Jalupro%20Classic%20-%202024_leaflet%203%20ante_WEB.pdf",
    productPackageImage: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/w1kzc70v_composizione-jalupro.png",
    composition: {
      vials: {
        count: "1 Vial",
        label: "Freeze Dried Amino Acids",
        items: ["Glycine", "L-Proline", "L-Lysine", "L-Leucine"]
      },
      ampoules: {
        count: "2 Ampoules (2x3 ml)",
        label: "30 mg Sodium Hyaluronate",
        detail: "200 kDa"
      }
    }
  },
  "jalupro-hmw": {
    name: "Jalupro HMW",
    tagline: "High Molecular Weight for Advanced Rejuvenation",
    description: "Jalupro HMW is an injectable dermal biorevitalization product created with a unique combination of high molecular weight hyaluronic acid and amino acid cluster, which is a powerful source for fibroblasts in the skin.",
    longDescription: `Jalupro HMW is an injectable dermal biorevitalization product created with a unique combination of high molecular weight hyaluronic acid and amino acid cluster, which is a powerful source for fibroblasts in the skin. It helps the skin produce its own hyaluronic acid, collagen and elastin fibers, supports slowing down the aging process and improving the skin, repairing the structure of the dermis, physiologically rejuvenating collagen and accelerating metabolism in the affected area.

With a higher molecular weight and greater viscosity, JALUPRO HMW is suitable for damaged and dry skin. High viscosity allows for longer hydration.

It can create a great protocol to deeply moisturize the skin and improve skin quality after the summer months when the skin is dry due to sun and heat.

In synergy with the AA cluster, it helps in longer stimulation of different HA receptors which will stimulate fibroblast activity and long-term stimulation of CD44 which will maintain homeostasis, trigger tissue repair and regeneration.

For a super-active protocol with high amounts of AA, Classic and HMW can be mixed, and LMW and HMW HA can be used together for immediate and long-lasting hydration.

It can also be preferred in elderly patients with less active metabolism.`,
    benefits: [
      "Helps the skin produce its own HA, collagen and elastin fibers",
      "Supports slowing down the aging process and repairing the dermis",
      "High viscosity allows for longer hydration",
      "Stimulates fibroblast activity and CD44 for tissue repair",
      "Can be mixed with Classic for a super-active protocol",
      "Suitable for elderly patients with less active metabolism"
    ],
    idealFor: [
      "Photo-aging and signs of aging (moderate and severe) on face and body",
      "Dry and dehydrated skin — Procedures in summer",
      "Strengthened protocol by mixing with Jalupro Classic on heavily damaged skin",
      "Perioral rejuvenation and biorestoration",
      "Before aesthetic procedures to support effectiveness for a longer time"
    ],
    treatmentAreas: ["Face", "Neck", "Décolleté", "Under arms", "Hands", "Inner legs", "Abdomen", "Knee area"],
    sessions: "3-4 sessions recommended, 3-4 weeks apart",
    results: "Progressive improvement, optimal results after full course, lasting 6-9 months",
    image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/eb2eafg2_image.png",
    productPackageImage: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/abmbmecc_JALUPRO-hmw_2020_01.png",
    brochureUrl: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/3ghft9bd_03_Jalupro%20HMW%20-%202024_leaflet%203%20ante_WEB.pdf",
    beforeAfterGallery: [
      {
        image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/f8fe7svh_image.png",
        caption: "Perioral region improvement"
      },
      {
        image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/mrxu4bye_JALUPRO%20HMW%2004.jpg",
        caption: "Full face rejuvenation and skin quality restoration"
      }
    ],
    composition: {
      vials: {
        count: "1 Vial",
        label: "Water Solutions — Amino Acids",
        items: ["Glycine", "L-Proline", "L-Lysine", "L-Leucine"]
      },
      ampoules: {
        count: "1 Syringe",
        label: "20 mg Sodium Hyaluronate",
        detail: "1600 kDa"
      }
    }
  },
  "jalupro-super-hydro": {
    name: "Jalupro Super Hydro",
    tagline: "V-Lift Deep Biorevitalization & Biorejuvenation",
    description: "Jalupro Super Hydro is an injectable biorejuvenation product developed for resource support of fibroblasts, maintenance of structural and functional activity of the extracellular matrix, increase of skin moisture level and biorestoration of ligaments.",
    longDescription: `Jalupro Super Hydro is an injectable biorejuvenation product developed for resource support of fibroblasts, maintenance of structural and functional activity of the extracellular matrix, increase of skin moisture level and biorestoration of ligaments. It contains a hybrid HA (hyaluronic acid of two different molecular sizes) with 7 amino acids supplemented with different amino acids with a patented amino acid cluster and enriched with 3 peptides.

Jalupro Super Hydro is a hybrid product between filler and mesotherapy. When applied to 6 selected points where the basic suspensory ligaments of the face are located with its high molecular weight and dense hyaluronic acid content, it mechanically supports the connective tissue here, thus helping to recover sagging skin.

In addition, with 7 functional amino acids and 3 peptides in its content, it supports the production of new collagen by stimulating the proliferation of cells in the connective tissue structure. The new fiber fibers formed in this way biologically repair the bonds. Unlike fillers, this mechanism helps to create a natural lifting from the first moment.

Applications with the right protocol help reduce and eliminate problems such as visible skin changes such as a visible recovery in the skin, a healthy, radiant skin tone, age-related wrinkles and fine lines, dull skin tone, loss of elasticity, volume loss and sagging.`,
    superHydroIndications: {
      intro: "JALUPRO Superhydro is a unique deep biorevitalization and biorejuvenation product. It helps restore collagen in deep connective tissue, especially suspended ligaments, and helps in connective tissue (ligament) biorestoration.",
      duration: "Each skin has different characteristics and conditions, so visible results may vary from person to person. However, results are usually seen immediately from the first session and usually last up to 6 months. It is recommended to give a maintenance injection after 6 months."
    },
    benefits: [
      "Resource support of fibroblasts and extracellular matrix",
      "Hybrid HA with 7 amino acids and 3 peptides",
      "Mechanically supports connective tissue at 6 ligament points",
      "Natural lifting from the first moment, unlike fillers",
      "Stimulates new collagen by proliferating connective tissue cells",
      "Biologically repairs bonds through new fiber formation"
    ],
    idealFor: [
      "Deep biorevitalization and biorejuvenation",
      "Connective tissue (ligament) biorestoration",
      "Visible skin recovery with healthy, radiant tone",
      "Age-related wrinkles, fine lines, and dull skin tone",
      "Loss of elasticity, volume loss, and sagging"
    ],
    treatmentAreas: ["Full face (6 ligament points)", "Neck", "Décolleté"],
    sessions: "Results seen immediately from first session, lasting up to 6 months",
    results: "Maintenance injection recommended after 6 months",
    image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/iw0mwh5x_image.png",
    productPackageImage: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/nsbsukpb_PACK_JALUPROSUPER%20HYDRO_BLU_ORO.jpg",
    compositionList: {
      format: "Prefilled Syringe 2.5 ml",
      components: [
        { category: "Amino Acids", items: "Glycine, Proline, Lysine, Leucine, Valine, Alanine, Arginine" },
        { category: "Peptides", items: "Acetyl Decapeptide-3, Oligopeptide-24, Acetyl Tetrapeptide-5" },
        { category: "Hyaluronic Acid", items: "LMW + HMW 3.2% HA" }
      ]
    },
    dualLayerContent: {
      image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/26upywic_image.png",
      title: "Beyond Skin, Beyond Aesthetics",
      subtitle: "Collagen as the Structural Foundation of the Body and the Importance of the Dual-Layer Approach",
      intro: "Collagen is more than just a component of youthful skin—it is the primary structural protein in the body, representing 70% of its component. This collagen content is critical to the integrity of connective tissues such as skin, retaining ligaments, retinacula cutis, fat tissue or even bones. Its role extends far beyond aesthetics, influencing the mechanical strength, resistance, stability of key anatomical structures, and homeostasis processes such as fibroblasts function.",
      sections: [
        {
          title: "Collagen in the Skin and Superficial ECM",
          content: "The extracellular matrix serves as the scaffolding for tissues, providing strength and resistance to external forces. Collagen fibers within the ECM maintain the tensile strength of the skin, provide elasticity and durability, and offer good scaffolding and tension to fibroblasts for optimal function. As collagen levels decline, the skin weakens, leading to visible signs of aging."
        },
        {
          title: "Collagen in Retaining Ligaments",
          content: "Retaining ligaments anchor soft tissues to underlying skeletal structures, contributing to facial contours and structural lift. Collagen provides lift by maintaining ligament tension and structure by stabilizing deeper layers of connective tissue. The Retinacula Cutis creates a dense network of subcutaneous connective tissue, offering a scaffold to adipocytes and cushioning the skin."
        },
        {
          title: "The Dual-Layer Approach",
          layers: [
            "Skin-Level Treatment: Rejuvenates the extracellular matrix to restore dermal thickness, strength, elasticity, hydration, and tone.",
            "Structural Support: Enhances the density and integrity of retaining ligaments and deeper connective tissues to restore lift and contour."
          ]
        }
      ]
    },
    ligamentAgingContent: {
      title: "Ligament Aging: The Role of Collagen and Fibroblasts",
      image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/8cu1brhv_image.png",
      intro: "Ligaments, like other connective tissues, are not immune to the aging process. They become progressively looser and weaker due to collagen degradation and fragmentation, leading to reduced tensile strength and structural support. This process significantly impacts the integrity of facial and body structures, as ligaments play a vital role in maintaining the alignment and stability of soft tissues.",
      retainingLigaments: {
        title: "The Importance of Retaining Ligaments",
        text: "Retaining ligaments function as anchoring structures, tethering the overlying skin and soft tissues to the deeper skeletal framework. Their role is critical for maintaining facial contours and preventing tissue sagging. With age, the degradation of collagen within these ligaments results in:",
        effects: [
          { term: "Laxity", desc: "A reduction in ligament tension leads to soft tissue displacement." },
          { term: "Loss of Definition", desc: "Fat compartments shift, contributing to sagging and a loss of youthful facial contours." }
        ],
        conclusion: "Addressing ligament health is essential for both functional and aesthetic restoration. A comprehensive approach to rejuvenation must include strategies to support and regenerate these deeper structures."
      },
      therapeuticImplication: {
        title: "Therapeutic Implication",
        text: "The cellular composition of ligaments is crucial for their function and repair:",
        cells: [
          { name: "Tenocytes (Fibrocytes)", desc: "Mature cells embedded in collagen fibers that maintain ligament integrity and homeostasis." },
          { name: "Tenoblasts (Fibroblasts)", desc: "Immature, spindle-shaped cells found in clusters, free from collagen fibers. These cells are highly proliferative and play a pivotal role in synthesizing collagen and other components of the extracellular matrix (ECM)." }
        ],
        detail: "The activity of tenoblasts is particularly important for ligament repair and regeneration. These cells respond to mechanical signals and biochemical cues, making them key targets for therapeutic interventions aimed at stimulating collagen synthesis.",
        insight: "Tenocytes are fibroblasts in a different location. And as fibroblasts, they need to be \"nourished\", in order to function properly and synthetize collagen, elastin and all the components of connective tissue."
      },
      cellularFoundation: {
        title: "The Cellular Foundation of Ligaments",
        text: "Aging ligaments require targeted support to maintain their structure and function. Strategies to address this include:",
        strategies: [
          { title: "Stimulating Fibroblast Activity", desc: "Providing the necessary resources, such as amino acids and cofactors, to promote collagen synthesis." },
          { title: "Supporting ECM Integrity", desc: "Enhancing the production of ECM components to restore ligament strength and elasticity." },
          { title: "Comprehensive Rejuvenation", desc: "Focusing on the entire connective tissue system, from the superficial layers to deep ligamentous structures, ensuring both functional and aesthetic outcomes." }
        ]
      }
    },
    liftProtocol: {
      image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/8mzxrojb_image.png",
      title: "L.I.F.T. Protocol",
      subtitle: "Ligaments and Fat Pads Technique"
    },
    brochureUrl: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/u5ujztnq_03_Jalupro%20SUPERHYDRO%20-%202024_leaflet%203%20ante_WEB.pdf",
    beforeAfterCombined: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/g1razvse_image.png",
    beforeAfterCaption: "Immediate lifting effect, improvement in skin texture, tone and firmness"
  },
  "jalupro-young-eye": {
    name: "Jalupro Young Eye",
    tagline: "Specialized Under-Eye Rejuvenation",
    description: "Skin of the eye area is the thinnest in the face and ages faster. It requires a bespoke formulation for revitalization of this delicate periocular area.",
    longDescription: `Skin of the eye area is the thinnest in the face and ages faster. It requires a bespoke formulation for revitalization of this delicate periocular area.

Contains amino acids to improve collagen production, skin density and thickness. Reduces periorbital wrinkles, dark circles and bags when related to thinning and sagging skin. Bi-density hyaluronic acid ensures hydration without increasing puffiness.`,
    clinicalNeeds: [
      "Reduce dark circles, puffiness, and fine lines around the eyes.",
      "Enhance skin firmness and elasticity in the delicate periocular region.",
      "Provide long-lasting hydration to smooth and plump under-eye skin."
    ],
    benefits: [
      "Amino acids improve collagen production, skin density and thickness",
      "Reduces periorbital wrinkles and dark circles",
      "Addresses bags related to thinning and sagging skin",
      "Bi-density hyaluronic acid ensures hydration without increasing puffiness",
      "Bespoke formulation for delicate periocular area",
      "Natural, refreshed eye appearance"
    ],
    idealFor: [
      "Dark circles under eyes",
      "Fine lines around eye area",
      "Crepey or thin under-eye skin",
      "Tired, aged eye appearance",
      "Patients avoiding surgical options"
    ],
    treatmentAreas: ["Under-eye area", "Crow's feet", "Upper eyelid (selected cases)"],
    sessions: "3-4 sessions recommended, 2-3 weeks apart",
    results: "Gradual improvement, best results after 4-6 weeks post-treatment",
    image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/ncrt7nbo_image.png",
    brochureUrl: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/iqq0jy2s_AW_JALUPRO%20YOUNG%20EYE%20BROCHURE_for%20view.pdf",
    beforeAfterCombined: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/k80vnkiu_image.png",
    beforeAfterCaption: "Periorbital rejuvenation — reduction in dark circles and improved under-eye skin quality",
    compositionList: {
      format: "1 Syringe",
      components: [
        { category: "7 Amino Acids", items: "" },
        { category: "3 Peptides", items: "" },
        { category: "Bidensity Sodium Hyaluronate", items: "" }
      ]
    },
    productPackageImage: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/wa49y3cz_image.png"
  },
  "jalupro-biofiller": {
    name: "Jalupro Biofiller",
    tagline: "Cross-Linked HA Filler with Amino Acids",
    description: "Jalupro Biofiller is an innovative injectable cross-linked HA that combines stabilized sodium hyaluronate with amino acids that stimulate collagen synthesis, providing both immediate skin volume restoration and long-term bioregenerative effects.",
    longDescription: `Jalupro Biofiller is an innovative injectable cross-linked HA that combines stabilized sodium hyaluronate with amino acids that stimulate collagen synthesis, providing both immediate skin volume restoration and long-term bioregenerative effects.

Formulated with a physiological pH buffer, the product is especially suitable for intradermal administration, enhancing skin quality, elasticity, and firmness. Unlike traditional fillers that only add volume, Jalupro Biofiller offers a dual action approach—providing immediate volumizing effects while simultaneously stimulating your skin's natural regenerative processes.

The unique formulation includes an amino acid complex (Glycine, L-proline, L-alanine, L-valine, L-lysine) that supports collagen and elastin synthesis, improves skin structure, and promotes natural regeneration. The phosphate buffer stabilizes pH, improves tolerability, and minimizes the risk of irritation.`,
    benefits: [
      "Dual action: filler + biostimulation",
      "Immediate volume restoration",
      "Long-term bioregenerative effects",
      "Stimulates collagen synthesis",
      "Natural and progressive results",
      "Excellent tolerability and high safety"
    ],
    idealFor: [
      "Patients seeking volume + skin quality improvement",
      "Those wanting natural-looking results",
      "Moderate volume loss in face",
      "Patients preferring dual-action treatments",
      "Those seeking long-lasting improvements"
    ],
    treatmentAreas: ["Face", "Cheeks", "Nasolabial folds", "Marionette lines", "Chin"],
    sessions: "1 session",
    results: "Immediate volume improvement, progressive skin quality enhancement over 2-3 months",
    image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/qiuc7abm_image.png",
    keyIngredients: [
      { name: "Cross-linked Hyaluronic Acid", desc: "Stabilized sodium hyaluronate for immediate volume restoration" },
      { name: "Amino Acid Complex", desc: "Glycine, L-proline, L-alanine, L-valine, L-lysine for collagen synthesis" },
      { name: "Phosphate Buffer", desc: "Stabilizes pH, improves tolerability, minimizes irritation" }
    ],
    biofillerVariants: [
      {
        name: "Biofiller Light",
        purpose: "Microfilling",
        image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/7td10hv6_image.png",
        description: "A microfilling approach planned by the physician that aims to soften the appearance of superficial fine lines and smooth the skin surface without creating a significant volume increase.",
        indications: ["Superficial fine lines", "Smoothing the skin surface", "Natural enhancement appearance without significant volume increase"],
        ha: "20 mg/mL — Biphasic HA",
        aa: "Slow-Release Patented AA Complex: Glycine, L-Proline, L-Alanine, L-Valine, L-Lysine",
        syringe: "1 x 1.1 mL — Ready-to-use syringe",
        needle: "30G needle, 27G cannula"
      },
      {
        name: "Biofiller Medium",
        purpose: "Re-volumizing",
        image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/wb89is5z_image.png",
        description: "A personalized approach planned by the physician that aims for a more defined contour and support while maintaining a natural appearance. Especially evaluated for needs such as moderate volume loss and prominent wrinkle appearance.",
        indications: ["Moderate volume loss", "Support for the appearance of deep wrinkles", "More defined contour while maintaining a natural look", "Subdermal planning approaches (physician decision)"],
        ha: "Monophasic, Cross-linked HA",
        aa: "Slow-Release Patented AA Complex: Glycine, L-Proline, L-Alanine, L-Valine, L-Lysine",
        syringe: "1 x 1.1 mL — Ready-to-use syringe",
        needle: "27G needle, 25G cannula"
      },
      {
        name: "Biofiller Deep",
        purpose: "Lifting · Projection · Support · Shaping",
        image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/w65k1wzt_image.png",
        description: "A physician-planned approach with lifting, contouring, volume, and shaping goals in areas where stronger structural support is required. In a comprehensive rejuvenation plan, it can be considered together with other Jalupro applications according to needs.",
        indications: ["Plans to support the lifting effect", "Contour clarity and the need for projection", "Volume and contouring focused applications", "Combined planning in a comprehensive rejuvenation approach"],
        ha: "Monophasic, Cross-linked HA",
        aa: "Slow-Release Patented AA Complex: Glycine, L-Proline, L-Alanine, L-Valine, L-Lysine",
        syringe: "1 x 1.1 mL — Ready-to-use syringe",
        needle: "27G needle, 25-23G cannula"
      }
    ]
  },
  "jalupro-glow-peel": {
    name: "Jalupro Glow Peel",
    tagline: "Add Some Magic to Your Jalupro Juvenation Tools",
    description: "Jalupro Glow Peel is an advanced skin renewal treatment for chrono and photo aging of face, neck, décolleté, hands and body. Experience immediate glow with no downtime, no flaking, and no pain.",
    longDescription: `Jalupro Glow Peel is an advanced chemical peel treatment designed to complement your Jalupro biorevitalization treatments. This innovative peel offers a unique combination of benefits that make it suitable for all patients and all seasons.

Unlike traditional chemical peels, Jalupro Glow Peel delivers remarkable results without the typical drawbacks—no downtime, no flaking, and no pain. Patients experience an immediate glow following treatment, making it perfect for those seeking quick, visible improvements.

The treatment works on multiple levels to address chrono and photo aging across the face, neck, décolleté, hands, and body, providing comprehensive skin renewal and rejuvenation.`,
    benefits: [
      "No downtime required",
      "No flaking or peeling",
      "No pain during treatment",
      "Immediate glow after treatment",
      "Suitable for all patients",
      "Can be used in all seasons"
    ],
    idealFor: [
      "Patients wanting no-downtime treatments",
      "Those seeking immediate visible results",
      "All skin types and conditions",
      "Year-round skin maintenance",
      "Complementary treatment to biorevitalization",
      "Chrono and photo aging concerns"
    ],
    treatmentAreas: ["Face", "Neck", "Décolleté", "Hands", "Body"],
    sessions: "Series of treatments recommended for optimal results",
    results: "Immediate glow, progressive improvement with continued treatments",
    image: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/b5b1mij2_GLOW-PEEL.png",
    beforeAfterCombined: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/kogyime7_image.png",
    brochureUrl: "https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/irg7qqjk_AW_JALUPRO%20GLOW%20PEEL%20BROCHURE%2022x22_AGG%2019022025_ai%20lady_TR_low.pdf",
    keyBenefits: [
      {
        category: "Biorevitalization",
        items: ["Renewal of cells", "Fine lines decrease", "Pigmentation decrease", "Collagen stimulation"]
      },
      {
        category: "Improve Skin Quality",
        items: ["Reduce pore size", "Improve skin tone and texture", "Tighten and firm up the skin"]
      },
      {
        category: "Decrease Imperfections",
        items: ["Acne scars reduction", "Stretch marks improvement"]
      }
    ],
    uniqueIngredients: [
      {
        name: "Trichloroacetate",
        description: "Gives the same effect of TCA but does not harm the skin. Allows good penetration and reduces the aggressivity of common peelings."
      },
      {
        name: "AHA Complex",
        description: "Combination of Tartaric, Lactobionic, Aminobutyric, and Citric acids for exfoliating, hydrating, and depigmenting effects—giving skin a radiant, compact, and smooth appearance."
      },
      {
        name: "Amino Acid Cluster",
        description: "Our signature amino acids feed the fibroblasts for enhanced collagen production."
      },
      {
        name: "Aminobutyric Acid (GABA)",
        description: "Increases production of epidermal cells, promotes synthesis of HA and collagen. In the dermis, promotes fibroblast proliferation, improving skin moisture and elasticity."
      },
      {
        name: "Ferulic Acid",
        description: "A natural plant-origin substance that is a powerful antioxidant like Vitamin C but more stable."
      }
    ],
    multifacetedActions: [
      "Rejuvenation & Revitalization",
      "Regeneration",
      "Hydration (Moisturization)",
      "Anti-inflammatory & Anti-acne",
      "Depigmenting action",
      "Antioxidant action",
      "Peeling (Exfoliation)",
      "Pore tightening & Sebum regulation"
    ]
  }
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "About", href: "#about" },
    { name: "Science", href: "#science" },
    { name: "Collagen", href: "#collagen" },
    { name: "Products", href: "#products" },
    { name: "How It Works", href: "#how-it-works" },
    { name: "For Clinics", href: "#clinics" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "header-glass shadow-sm" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/lmrqa5t0_image.png" 
              alt="Jalupro" 
              className="h-8 md:h-10 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-sm font-medium text-text-muted hover:text-primary transition-colors link-underline"
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <a
              href="#contact"
              className="btn-primary text-sm rounded-sm"
              data-testid="nav-cta-button"
            >
              Get In Touch
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <CloseIcon size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`md:hidden fixed inset-0 top-20 bg-white z-40 transition-transform duration-300 ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="mobile-menu"
      >
        <div className="flex flex-col p-6 gap-4">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className="text-lg font-medium text-primary py-3 border-b border-gray-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.name}
            </a>
          ))}
          <a
            href="#contact"
            className="btn-primary text-center mt-4 rounded-sm"
            onClick={() => setMobileMenuOpen(false)}
          >
            Get In Touch
          </a>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      className="relative min-h-screen flex items-center pt-20"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1728727242233-0924178c1fb1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHx3b21hbiUyMGdsb3dpbmclMjBza2luJTIwbmF0dXJhbHxlbnwwfHx8fDE3NzUzNjE4Nzd8MA&ixlib=rb-4.1.0&q=85"
          alt="Natural glowing skin"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-2xl"
        >
          <motion.p 
            variants={fadeInUp}
            className="text-xs font-semibold tracking-[0.2em] uppercase text-text-muted mb-6"
          >
            Rethinking Biorevitalization
          </motion.p>
          
          <motion.h1 
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight text-slate-900 mb-6"
          >
            The Age of
            <span className="block mt-2">
              <em className="italic">Regenerative Medicine</em>
            </span>
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-base lg:text-lg leading-relaxed text-text-muted mb-10 max-w-xl"
          >
            Advanced biorevitalization powered by amino acids and hyaluronic acid. 
            Clinically proven to stimulate collagen and restore skin quality naturally.
          </motion.p>
          
          <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
            <a
              href="#about"
              className="btn-primary rounded-sm inline-flex items-center gap-2"
              data-testid="hero-discover-btn"
            >
              Discover Jalupro
              <ArrowRight size={18} weight="light" />
            </a>
            <a
              href="#clinics"
              className="btn-secondary rounded-sm"
              data-testid="hero-clinics-btn"
            >
              For Clinics
            </a>
            <a
              href="https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/dubzlk5w_AW_Jalupro%20Global%20completo%202026_LOW%20DEF.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download
              className="btn-secondary rounded-sm inline-flex items-center gap-2"
              data-testid="hero-download-brochure-btn"
            >
              <FileArrowDown size={18} /> Download Brochure
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// What is Jalupro Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 lg:py-32 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Main Intro */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          {/* Image */}
          <AnimatedSection>
            <div className="image-zoom-container rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/2t87obwl_image.png"
                alt="Jalupro treatment results - radiant skin"
                className="w-full aspect-[4/5] object-cover object-top"
              />
            </div>
          </AnimatedSection>

          {/* Content */}
          <AnimatedSection>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-text-muted mb-4">
              The Science Behind Jalupro
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 mb-6">
              Designed in Switzerland, Born from Research
            </h2>
            <p className="text-base lg:text-lg leading-relaxed text-text-muted mb-6">
              Jalupro is designed in our laboratories in Switzerland, where advanced technologies 
              are researched by Professionel Derma, a Swiss biotechnology company focused on the 
              development of innovative medical devices to treat different pathologies.
            </p>
            <p className="text-base leading-relaxed text-text-muted mb-6">
              The history of Jalupro products has begun the research of the famous Italian Prof. Dr. Franco Conti, 
              cardiologist, pneumologist, internist and pharmacologist. Prof. Conti was one of the first to investigate 
              the role of amino acids in human health and dermatology. His passion for dermatological aspects of skin 
              treatments, his desire to improve patients' quality of life and extend life expectancy have led him to 
              create unique products based on a cluster of amino acids specific to collagen production.
            </p>
            <p className="text-sm text-slate-500 italic">
              As a result of Prof. Conti's research and the work of a highly qualified team, the Jalupro brand was 
              born in 2004 and today it has become a frequently preferred brand among skin rejuvenation procedures.
            </p>

            {/* ART Logo */}
            <div className="mt-8 pt-8 border-t border-slate-100">
              <img
                src="https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/h1qt5d87_image.png"
                alt="ART - Amino Acid Replacement Therapy"
                className="h-20 w-auto"
              />
            </div>
          </AnimatedSection>
        </div>

        {/* Patented Amino Acid Section */}
        <AnimatedSection>
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-14">
            <div className="max-w-4xl mx-auto text-center mb-10">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-4">
                Jalupro Patented Amino Acid Combination
              </p>
              <h3 className="font-heading text-2xl sm:text-3xl font-light text-slate-900 mb-3">
                Beauty Comes From Within
              </h3>
              <div className="w-16 h-px bg-primary mx-auto mb-8"></div>
              <p className="text-text-muted leading-relaxed text-base mb-6">
                We lose 1% of collagen every year. From the age of 25, collagen begins to decline, 
                causing the first signs of aging to appear, and this loss causes the deterioration of 
                connective tissues, skin and hair.
              </p>
            </div>

            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h4 className="font-heading text-lg font-medium text-slate-900 mb-3">
                  Why amino acids and not collagen directly?
                </h4>
                <p className="text-text-muted text-sm leading-relaxed">
                  Collagen cannot be directly absorbed by the body but must first be digested, making it 
                  partially bioavailable (less than 20%). Jalupro contains only the exact composition of amino 
                  acids that the body needs to produce collagen, maximizing its assimilation and achievable results.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h4 className="font-heading text-lg font-medium text-slate-900 mb-3">
                  Research-Backed Formulation
                </h4>
                <p className="text-text-muted text-sm leading-relaxed">
                  It has been shown how the integration of amino acids directly promotes collagen production in the 
                  deep layers of the skin. Thanks to years of research, the amino acids used in Jalupro products 
                  are among the high-purity ingredients produced in the best quality. Each of our formulas has been 
                  carefully tested to guarantee maximum safety and effectiveness.
                </p>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Biohacking Science Section
const BiohackingSection = () => {
  const features = [
    {
      icon: <Dna size={28} weight="thin" />,
      title: "Fibroblast Activation",
      description: "Targets the cellular 'factories' that produce collagen and elastin"
    },
    {
      icon: <Atom size={28} weight="thin" />,
      title: "Amino Acid Delivery",
      description: "Provides glycine, proline, and lysine - essential building blocks"
    },
    {
      icon: <Lightning size={28} weight="thin" />,
      title: "Regenerative Results",
      description: "Goes beyond surface repair to create lasting structural change"
    }
  ];

  return (
    <section id="science" className="py-24 lg:py-32 bg-[#0a1628] text-white relative overflow-hidden" data-testid="biohacking-section">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0f2847] to-[#0a1628] opacity-90"></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <AnimatedSection>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400 mb-4">
              Regenerative Science
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-white mb-6">
              Biohacking for
              <span className="block mt-2">
                <em className="italic text-cyan-300">Better Aging</em>
              </span>
            </h2>
            <p className="text-base lg:text-lg leading-relaxed text-slate-300 mb-6">
              "Biohacking" isn't just for tech enthusiasts—it's an innovative approach to improving 
              how your body functions. In the context of aging, biohacking focuses on optimizing 
              fibroblast activity, the "factories" in your skin that produce collagen and elastin.
            </p>
            <p className="text-base leading-relaxed text-slate-400 mb-8">
              Fibroblasts rely on specific amino acids (like glycine, proline, and lysine) as their 
              raw materials. When these resources are depleted, collagen production slows down, and 
              signs of aging accelerate. Advanced treatments like Jalupro provide your fibroblasts 
              with the precise building blocks they need.
            </p>

            <div className="space-y-5 mb-10">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center flex-shrink-0 text-cyan-400">
                    {feature.icon}
                  </div>
                  <div>
                    <h4 className="font-heading text-lg font-medium text-white mb-1">
                      {feature.title}
                    </h4>
                    <p className="text-slate-400 text-sm">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Why It Matters callout */}
            <div className="bg-gradient-to-r from-cyan-500/10 to-transparent border-l-2 border-cyan-400 pl-6 py-4">
              <h4 className="font-heading text-lg font-medium text-cyan-300 mb-2">Why It Matters</h4>
              <p className="text-slate-300 text-sm leading-relaxed">
                Using targeted amino acid therapy, Jalupro supports fibroblast function to produce 
                long-lasting improvements. This approach doesn't just repair; it regenerates, ensuring 
                your skin can maintain elasticity, hydration, and structural integrity.
              </p>
            </div>
          </AnimatedSection>

          {/* Image */}
          <AnimatedSection>
            <div className="relative">
              {/* Glow effect behind image */}
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 blur-3xl rounded-3xl"></div>
              <div className="relative image-zoom-container rounded-2xl overflow-hidden border border-cyan-500/20">
                <img
                  src="https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/y2sv1v80_image.png"
                  alt="Biohacking and regenerative science"
                  className="w-full aspect-[4/5] object-cover"
                />
                {/* Subtle overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/60 via-transparent to-transparent"></div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Collagen Section
const CollagenSection = () => {
  const agingEffects = [
    "Sagging and sliding of fat compartments",
    "Thinning of the skin",
    "A loss of structural support"
  ];

  return (
    <section id="collagen" className="py-24 lg:py-32 bg-gradient-to-b from-[#1a3a4a] to-[#0f2a3a] text-white relative overflow-hidden" data-testid="collagen-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image - Left Side */}
          <AnimatedSection>
            <div className="relative">
              <div className="relative image-zoom-container rounded-2xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/x7a1x34k_image.png"
                  alt="Collagen makes up 70% of skin structure"
                  className="w-full aspect-square object-cover"
                />
              </div>
            </div>
          </AnimatedSection>

          {/* Content - Right Side */}
          <AnimatedSection>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-teal-300 mb-4">
              Skin Foundation
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-white mb-6">
              Collagen in the Face
            </h2>
            <p className="text-base lg:text-lg leading-relaxed text-slate-200 mb-6">
              Collagen makes up <strong className="text-teal-300">70% of the face's connective tissue</strong>. 
              It provides the skin with firmness, health and resilience. At deeper level, collagen is essential 
              for the strength of retaining ligaments, maintaining lift and structure, preventing the sagging 
              and loss of definition that often accompanies aging.
            </p>

            {/* Aging Impact */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
              <h3 className="font-heading text-xl font-medium text-white mb-4">
                The Visible Impact of Collagen on Aging
              </h3>
              <p className="text-slate-300 text-sm mb-4">
                With time, collagen within the skin and deep connective tissues, such as ligaments and fat pads, 
                deteriorates and decreases. This leads to visible changes:
              </p>
              <ul className="space-y-2">
                {agingEffects.map((effect, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0"></div>
                    {effect}
                  </li>
                ))}
              </ul>
              <p className="text-slate-400 text-sm mt-4 italic">
                These changes manifest as fine lines, wrinkles, volume loss, and diminished facial contours.
              </p>
            </div>

            {/* Regeneration Callout */}
            <div className="bg-gradient-to-r from-teal-500/20 to-transparent border-l-2 border-teal-400 pl-6 py-4">
              <h4 className="font-heading text-lg font-medium text-teal-300 mb-2">
                Collagen Regeneration: The Key to Proactive Aging
              </h4>
              <p className="text-slate-300 text-sm leading-relaxed">
                To truly combat aging, you need to maintain and regenerate collagen—ensuring long-term ongoing 
                collagen production. Regenerative treatments target the physiological pathway of collagen 
                production and fibroblast function, restoring the skin's natural ability to rebuild, strengthen, 
                and maintain its optimal structure, resulting in a more youthful, resilient appearance expressing 
                health, resilience, and radiance.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Collagen Deep Dive Section
const CollagenDeepSection = () => {
  const whyLosingCollagen = [
    {
      category: "Nutritional Deficiencies",
      items: [
        "Decreased protein intake or poor-quality protein sources",
        "Reduced protein digestion due to aging-related declines in stomach acid",
        "Imbalances in gut microbiota impairing nutrient absorption"
      ]
    },
    {
      category: "Physiological Competition",
      items: [
        "Body prioritizes amino acids for vital functions",
        "Collagen synthesis competes with muscle protein synthesis",
        "Shortage of resources at the skin level"
      ]
    },
    {
      category: "Environmental Factors",
      items: [
        "Oxidative stress and sun exposure",
        "Smoking and poor sleep habits",
        "Accelerated collagen breakdown from lifestyle"
      ]
    }
  ];

  const consequences = [
    { title: "Structural Weakening", desc: "Leading to sagging skin, wrinkles, and reduced elasticity" },
    { title: "Decreased Repair Capacity", desc: "Slower recovery from tissue damage" },
    { title: "Scarcity of Resources", desc: "Reduced availability of amino acids and cofactors" }
  ];

  return (
    <section className="py-24 lg:py-32 bg-gradient-to-b from-[#f8f9fc] to-[#f0f4f8]" data-testid="collagen-deep-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header with Image */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <AnimatedSection>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-rose-400 mb-4">
              Understanding Collagen
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 mb-6">
              Collagen's Role in
              <span className="block mt-2">
                <em className="italic text-rose-500">Radiant Health</em>
              </span>
            </h2>
            <p className="text-base lg:text-lg leading-relaxed text-slate-600 mb-6">
              Collagen is the cornerstone of connective tissues, with <strong className="text-slate-800">70% of its composition</strong>. 
              Those strong-as-steel fibers form the architecture—the structural framework of the skin, tendons, ligaments, 
              and extracellular matrices.
            </p>
            <p className="text-base leading-relaxed text-slate-500">
              Its synthesis is made by <strong className="text-slate-700">fibroblasts</strong>, specialized cells that play a crucial role 
              in maintaining tissue integrity, elasticity, and repair. The production of collagen relies heavily on specific 
              amino acids—primarily <em>glycine, proline, and lysine</em>—which serve as the building blocks of its triple-helix structure.
            </p>
          </AnimatedSection>

          <AnimatedSection>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-rose-200/40 to-sky-200/40 blur-3xl rounded-3xl"></div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/5wvexeq7_image.png"
                  alt="Amino acid molecules - building blocks of collagen"
                  className="w-full aspect-square object-cover"
                />
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* How Fibroblasts Create Collagen */}
        <AnimatedSection className="mb-20">
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm border border-slate-100">
            <h3 className="font-heading text-2xl sm:text-3xl font-light text-slate-900 mb-6">
              How Fibroblasts Create Collagen
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              Think about proteins like collagen as a chain of amino acids. Each protein has its specific melody, and 
              collagen's sequence is centered around <strong className="text-rose-500">3 key amino acids</strong> to give collagen fibers 
              their strength and stability.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Without adequate levels of these amino acids, collagen production falters. Studies have shown that we often 
              have a chronic deficit in these amino acids, from lifestyle reasons or even evolutionary reasons. 
              <strong className="text-primary"> Treatments like Jalupro replenish these key resources</strong>, promoting the physiological 
              restoration of connective tissue and improving both appearance and overall vitality.
            </p>
          </div>
        </AnimatedSection>

        {/* Why We're Losing Collagen */}
        <AnimatedSection className="mb-20">
          <div className="text-center mb-12">
            <h3 className="font-heading text-2xl sm:text-3xl font-light text-slate-900 mb-4">
              Why Are We Losing Collagen?
            </h3>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Collagen levels begin to decline naturally in our 20s, with an annual loss of <strong className="text-slate-700">1–2%</strong>. 
              However, this decline can be accelerated by several factors:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {whyLosingCollagen.map((section, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                <h4 className="font-heading text-lg font-medium text-slate-800 mb-4 pb-3 border-b border-slate-100">
                  {section.category}
                </h4>
                <ul className="space-y-3">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-2 flex-shrink-0"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Consequences */}
        <AnimatedSection className="mb-20">
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 lg:p-12 text-white">
            <h3 className="font-heading text-2xl sm:text-3xl font-light mb-6">
              The Consequences of Collagen Loss
            </h3>
            <p className="text-slate-300 mb-8">
              As collagen production slows and destruction increases, connective tissues face significant challenges:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {consequences.map((item, index) => (
                <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-5">
                  <h4 className="font-medium text-rose-300 mb-2">{item.title}</h4>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-400 mt-8 text-sm italic">
              This imbalance between production and breakdown leads to the visible and functional signs of aging.
            </p>
          </div>
        </AnimatedSection>

        {/* The Path Forward & Jalupro Solution */}
        <AnimatedSection>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-100">
              <h3 className="font-heading text-xl font-medium text-slate-900 mb-4">
                The Path Forward
              </h3>
              <p className="text-slate-600 leading-relaxed">
                To combat collagen loss, targeted interventions that support fibroblast function are essential. 
                Nutritional strategies, amino acid supplementation, and advanced treatments can restore the resources 
                needed for optimal collagen biosynthesis. Supporting fibroblasts ensures not only the preservation of 
                skin health but also the maintenance of the structural integrity of connective tissues throughout the body.
              </p>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-sky-50 rounded-xl p-8 border border-rose-100">
              <h3 className="font-heading text-xl font-medium text-primary mb-4">
                How Jalupro Can Help
              </h3>
              <p className="text-slate-700 leading-relaxed">
                Jalupro offers a scientifically formulated amino acid cluster specifically designed to target the root 
                causes of collagen loss. By replenishing essential amino acids such as <strong>glycine, proline, and lysine</strong>, 
                Jalupro provides the raw materials fibroblasts need to synthesize and regenerate collagen effectively.
              </p>
              <a 
                href="#contact" 
                className="inline-flex items-center gap-2 mt-6 text-primary font-medium hover:gap-3 transition-all"
                data-testid="collagen-deep-cta"
              >
                Learn more about Jalupro treatments <ArrowRight size={18} weight="light" />
              </a>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Product Categories Section - SEO Optimized
const ProductsSection = () => {
  const injectableProducts = [
    {
      name: "Jalupro Classic",
      slug: "jalupro-classic",
      description: "Improves skin hydration and early signs of ageing. Ideal for patients beginning their skin rejuvenation journey in New Zealand."
    },
    {
      name: "Jalupro HMW",
      slug: "jalupro-hmw",
      description: "Enhances skin density and elasticity for more advanced ageing. High molecular weight formula for deeper skin restoration."
    },
    {
      name: "Jalupro Super Hydro",
      slug: "jalupro-super-hydro",
      description: "A next-generation biorevitalization treatment combining structural support and deep hydration for comprehensive skin renewal."
    },
    {
      name: "Jalupro Young Eye",
      slug: "jalupro-young-eye",
      description: "Specially formulated for delicate under-eye rejuvenation. Targets dark circles, fine lines, and skin laxity around the eyes."
    }
  ];

  const haBenefits = [
    "Restores skin structure",
    "Improves tissue quality",
    "Provides deep hydration",
    "Maintains natural facial appearance"
  ];

  const peelingBenefits = [
    "Promotes skin cell turnover",
    "Improves pigmentation and uneven tone",
    "Reduces acne scars and imperfections",
    "Stimulates collagen production"
  ];

  return (
    <section id="products" className="py-24 lg:py-32 bg-accent-grey" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <AnimatedSection className="text-center mb-16">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-text-muted mb-4">
            Product Range
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900">
            Jalupro Skin Booster Treatments NZ
          </h2>
          <p className="text-text-muted mt-4 max-w-2xl mx-auto">
            Comprehensive range of injectable skin rejuvenation and collagen stimulation treatments available in New Zealand.
          </p>
        </AnimatedSection>

        {/* Injectable Biorevitalization */}
        <AnimatedSection className="mb-12">
          <div className="bg-white border border-slate-100 p-8 lg:p-12 rounded-xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="text-primary"><Syringe size={32} weight="thin" /></div>
              <h3 className="font-heading text-2xl sm:text-3xl font-light text-slate-900">
                Injectable Biorevitalization
              </h3>
            </div>
            <p className="text-text-muted mb-8 max-w-3xl">
              Jalupro's injectable range is designed to target different levels of skin ageing and regeneration. 
              Ideal for patients seeking collagen stimulation and natural skin rejuvenation in New Zealand.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {injectableProducts.map((product, index) => (
                <Link 
                  key={index} 
                  to={`/products/${product.slug}`}
                  className="bg-accent-grey/50 p-6 rounded-lg hover:bg-accent-blush transition-all duration-300 group cursor-pointer block"
                  data-testid={`product-link-${product.slug}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-heading text-lg font-medium text-slate-800 group-hover:text-primary transition-colors">
                      {product.name}
                    </h4>
                    <ArrowRight size={18} weight="light" className="text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>
                  <p className="text-sm text-slate-600">{product.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* HA Biofillers & Peeling Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Jalupro Biofiller - Clickable */}
          <AnimatedSection>
            <Link 
              to="/products/jalupro-biofiller"
              className="bg-white border border-slate-100 p-8 rounded-xl h-full block hover:border-primary/30 hover:shadow-lg transition-all duration-300 group"
              data-testid="product-link-jalupro-biofiller"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="text-primary"><Drop size={32} weight="thin" /></div>
                  <h3 className="font-heading text-xl sm:text-2xl font-light text-slate-900 group-hover:text-primary transition-colors">
                    Jalupro Biofiller
                  </h3>
                </div>
                <ArrowRight size={20} weight="light" className="text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </div>
              <p className="text-xs font-semibold tracking-wider uppercase text-primary mb-3">
                Cross-Linked HA Filler with Amino Acids
              </p>
              <p className="text-text-muted mb-6">
                An innovative injectable cross-linked HA combining stabilized sodium hyaluronate with amino acids 
                for immediate volume restoration and long-term bioregenerative effects.
              </p>
              <div className="space-y-3">
                <p className="text-xs font-semibold tracking-wider uppercase text-text-muted">Advantages:</p>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={14} weight="bold" className="text-primary flex-shrink-0" />
                  Natural and progressive results
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={14} weight="bold" className="text-primary flex-shrink-0" />
                  Dual action: filler + biostimulation
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={14} weight="bold" className="text-primary flex-shrink-0" />
                  Excellent tolerability and high safety
                </div>
              </div>
            </Link>
          </AnimatedSection>

          {/* Peeling - Clickable */}
          <AnimatedSection>
            <Link 
              to="/products/jalupro-glow-peel"
              className="bg-white border border-slate-100 p-8 rounded-xl h-full block hover:border-primary/30 hover:shadow-lg transition-all duration-300 group"
              data-testid="product-link-jalupro-glow-peel"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="text-primary"><Sparkle size={32} weight="thin" /></div>
                  <h3 className="font-heading text-xl sm:text-2xl font-light text-slate-900 group-hover:text-primary transition-colors">
                    Jalupro Glow Peel
                  </h3>
                </div>
                <ArrowRight size={20} weight="light" className="text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-all" />
              </div>
              <p className="text-xs font-semibold tracking-wider uppercase text-primary mb-3">
                Advanced Chemical Peel
              </p>
              <p className="text-text-muted mb-6">
                An advanced skin renewal treatment designed to enhance skin texture and radiance. 
                Perfect as a complementary treatment to injectable biorevitalization.
              </p>
              <div className="space-y-3">
                <p className="text-xs font-semibold tracking-wider uppercase text-text-muted">Benefits:</p>
                {peelingBenefits.map((benefit, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                    <Check size={14} weight="bold" className="text-primary flex-shrink-0" />
                    {benefit}
                  </div>
                ))}
              </div>
            </Link>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// How It Works Section - SEO Optimized
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Injection",
      description: "A precise combination of amino acids (glycine, proline, lysine, leucine) and hyaluronic acid is delivered directly into the skin using fine needles."
    },
    {
      number: "02",
      title: "Cellular Activation",
      description: "Fibroblasts are stimulated, initiating collagen and elastin production. The amino acids provide essential building blocks for natural skin regeneration."
    },
    {
      number: "03",
      title: "Skin Regeneration",
      description: "The skin becomes firmer, more hydrated, and visibly rejuvenated over time. Results are progressive and long-lasting."
    }
  ];

  return (
    <section id="how-it-works" className="py-24 lg:py-32 bg-white" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <AnimatedSection className="text-center mb-20">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-text-muted mb-4">
            The Process
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900">
            How Jalupro Works
          </h2>
          <p className="text-text-muted mt-4 max-w-2xl mx-auto">
            Jalupro works through a targeted, three-step mechanism that ensures progressive, natural improvement in skin quality.
          </p>
        </AnimatedSection>

        <div className="grid md:grid-cols-3 gap-8 lg:gap-16 mb-16">
          {steps.map((step, index) => (
            <AnimatedSection key={index} className="relative">
              {/* Large background number */}
              <span className="step-number select-none">{step.number}</span>
              
              <div className="relative z-10 pt-16">
                <h3 className="font-heading text-2xl font-light text-slate-900 mb-4">
                  {step.title}
                </h3>
                <p className="text-text-muted leading-relaxed">
                  {step.description}
                </p>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection className="text-center">
          <p className="text-slate-500 italic max-w-2xl mx-auto">
            This process ensures progressive, natural improvement in skin quality without creating artificial volume.
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Treatment Benefits Section - SEO Optimized with Treatment Areas & Who Is Jalupro For
const BenefitsSection = () => {
  const benefits = [
    "Smoother, firmer skin",
    "Improved hydration and glow",
    "Reduction in fine lines and wrinkles",
    "Enhanced skin elasticity",
    "Natural, refreshed appearance"
  ];

  const treatmentAreas = [
    "Face",
    "Under-eye area",
    "Neck and décolletage",
    "Hands",
    "Body areas with skin laxity"
  ];

  const idealFor = [
    "Want natural anti-aging treatments",
    "Prefer skin quality improvement over fillers",
    "Experience dehydration, dullness, or fine lines",
    "Seek collagen stimulation treatments in New Zealand",
    "Want preventative skin ageing solutions"
  ];

  return (
    <section className="py-24 lg:py-32 bg-accent-blush" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Results & Benefits */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <AnimatedSection>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-text-muted mb-4">
              Results & Benefits
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 mb-6">
              Jalupro Treatment Results
            </h2>
            <p className="text-text-muted mb-8">
              Patients can expect progressive, long-lasting results focusing on skin regeneration rather than volume enhancement.
            </p>
            
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <Check size={16} weight="bold" className="text-primary" />
                  </div>
                  <p className="text-lg text-slate-800">{benefit}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="image-zoom-container rounded-2xl overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/qw5jfbvs_image.png"
                alt="Jalupro treatment results in New Zealand"
                className="w-full aspect-square object-cover object-top"
              />
            </div>
          </AnimatedSection>
        </div>

        {/* Treatment Areas & Who Is Jalupro For */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Treatment Areas */}
          <AnimatedSection>
            <div className="bg-white rounded-xl p-8 h-full">
              <h3 className="font-heading text-2xl font-light text-slate-900 mb-6">
                Treatment Areas
              </h3>
              <p className="text-text-muted mb-6">
                Jalupro treatments in New Zealand can be used on multiple areas:
              </p>
              <div className="space-y-3">
                {treatmentAreas.map((area, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0"></div>
                    <p className="text-slate-700">{area}</p>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* Who Is Jalupro For */}
          <AnimatedSection>
            <div className="bg-white rounded-xl p-8 h-full">
              <h3 className="font-heading text-2xl font-light text-slate-900 mb-6">
                Who Is Jalupro For?
              </h3>
              <p className="text-text-muted mb-6">
                Jalupro is ideal for patients who:
              </p>
              <div className="space-y-3">
                {idealFor.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={16} weight="bold" className="text-primary flex-shrink-0" />
                    <p className="text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// For Clinics Section - SEO Optimized
const ClinicsSection = () => {
  const whyChoose = [
    "Evidence-based formulations",
    "Strong global reputation in aesthetic medicine",
    "High patient satisfaction and repeat treatments",
    "Training and professional support available",
    "Premium positioning within the aesthetic market"
  ];

  return (
    <section id="clinics" className="py-24 lg:py-32 bg-white" data-testid="clinics-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <AnimatedSection>
            <div className="image-zoom-container rounded-2xl overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/a79rflep_image.png"
                alt="Professional aesthetic consultation in clinic"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-text-muted mb-4">
              For Clinics
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 mb-6">
              Partner With Jalupro New Zealand
            </h2>
            <p className="text-base lg:text-lg leading-relaxed text-text-muted mb-8">
              Jalupro offers aesthetic clinics in New Zealand access to a clinically proven, 
              globally recognised skin biorevitalization system.
            </p>

            <div className="mb-8">
              <h3 className="font-heading text-lg font-medium text-slate-800 mb-4">Why Choose Jalupro:</h3>
              <div className="space-y-3">
                {whyChoose.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={16} weight="bold" className="text-primary flex-shrink-0" />
                    <p className="text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <a
                href="#contact"
                className="btn-primary rounded-sm"
                data-testid="become-partner-btn"
              >
                Become a Partner
              </a>
              <a
                href="#contact"
                className="btn-secondary rounded-sm"
                data-testid="request-info-btn"
              >
                Request Information
              </a>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Before & After Section
const BeforeAfterSection = () => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const handleMove = (clientX) => {
    if (!containerRef.current || !isDragging.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100);
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleMouseMove = (e) => handleMove(e.clientX);
  const handleTouchMove = (e) => handleMove(e.touches[0].clientX);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <section className="py-24 lg:py-32 bg-accent-grey" data-testid="before-after-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <AnimatedSection className="text-center mb-16">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-text-muted mb-4">
            Results
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 mb-4">
            Before & After
          </h2>
          <p className="text-text-muted max-w-2xl mx-auto">
            See the natural, progressive results achieved through Jalupro biorevitalization treatments.
          </p>
        </AnimatedSection>

        <AnimatedSection>
          <div 
            ref={containerRef}
            className="relative max-w-3xl mx-auto aspect-[3/2] rounded-2xl overflow-hidden cursor-ew-resize select-none"
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleMouseUp}
            data-testid="before-after-slider"
          >
            {/* After Image (Background) */}
            <img
              src="https://images.unsplash.com/photo-1519084278803-b94f11e1c63b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHx3b21hbiUyMGdsb3dpbmclMjBza2luJTIwbmF0dXJhbHxlbnwwfHx8fDE3NzUzNjE4Nzd8MA&ixlib=rb-4.1.0&q=85"
              alt="After treatment"
              className="absolute inset-0 w-full h-full object-cover"
            />
            
            {/* Before Image (Clipped) */}
            <div 
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <img
                src="https://images.pexels.com/photos/9775369/pexels-photo-9775369.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Before treatment"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            
            {/* Slider Handle */}
            <div 
              className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
              style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
                <div className="flex gap-0.5">
                  <div className="w-0.5 h-4 bg-primary rounded-full"></div>
                  <div className="w-0.5 h-4 bg-primary rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Labels */}
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-3 py-1.5 text-sm font-medium text-primary">
              Before
            </div>
            <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur px-3 py-1.5 text-sm font-medium text-primary">
              After
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection className="text-center mt-8">
          <p className="text-sm text-text-muted italic">
            *Individual results may vary. Images shown are for illustrative purposes.
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
};

// Contact Section - SEO Optimized
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    clinic_name: "",
    country: "New Zealand",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you for your inquiry. We'll be in touch soon!");
      setFormData({
        name: "",
        email: "",
        clinic_name: "",
        country: "New Zealand",
        message: ""
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <AnimatedSection className="text-center mb-16">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-text-muted mb-4">
            Contact Us
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 mb-4">
            Jalupro Treatments & Partnerships in New Zealand
          </h2>
          <p className="text-text-muted max-w-2xl mx-auto">
            Interested in Jalupro treatments or becoming a clinic partner in New Zealand? 
            Contact us today to learn more about Jalupro skin booster treatments.
          </p>
        </AnimatedSection>

        <div className="grid lg:grid-cols-2 gap-16">
          <AnimatedSection>
            <h3 className="font-heading text-2xl font-light text-slate-900 mb-6">
              Get In Touch
            </h3>
            <p className="text-text-muted mb-8">
              Whether you're a clinic looking to partner with us or a patient seeking 
              information about injectable skin rejuvenation in NZ, we'd love to hear from you.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent-grey flex items-center justify-center text-primary">
                  <EnvelopeSimple size={24} weight="thin" />
                </div>
                <div>
                  <p className="text-sm text-text-muted">Email</p>
                  <p className="font-medium">info@jalupro.co.nz</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-accent-grey flex items-center justify-center text-primary">
                  <MapPin size={24} weight="thin" />
                </div>
                <div>
                  <p className="text-sm text-text-muted">Location</p>
                  <p className="font-medium">Auckland, New Zealand</p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name *"
                  required
                  className="form-input-clean"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address *"
                  required
                  className="form-input-clean"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="clinic_name"
                  value={formData.clinic_name}
                  onChange={handleChange}
                  placeholder="Clinic Name (Optional)"
                  className="form-input-clean"
                  data-testid="contact-clinic-input"
                />
              </div>
              <div>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="form-input-clean bg-transparent"
                  data-testid="contact-country-select"
                >
                  <option value="New Zealand">New Zealand</option>
                  <option value="Australia">Australia</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your Message *"
                  required
                  rows={4}
                  className="form-input-clean resize-none"
                  data-testid="contact-message-textarea"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    company: [
      { name: "About Jalupro", href: "#about" },
      { name: "Products", href: "#products" },
      { name: "How It Works", href: "#how-it-works" },
      { name: "For Clinics", href: "#clinics" }
    ],
    legal: [
      { name: "Privacy Policy", href: "#" },
      { name: "Terms of Service", href: "#" },
      { name: "Cookie Policy", href: "#" }
    ]
  };

  return (
    <footer className="bg-primary text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_jalupro-nz/artifacts/lmrqa5t0_image.png" 
              alt="Jalupro" 
              className="h-10 w-auto mb-4"
            />
            <p className="text-white/70 text-sm leading-relaxed max-w-md mb-4">
              Rethinking Biorevitalization. The age of Regenerative Medicine.
            </p>
            <p className="text-white/50 text-xs leading-relaxed max-w-md mb-6">
              Advanced treatments combining amino acids and hyaluronic acid 
              to stimulate collagen and restore skin quality from within.
            </p>
            <div className="flex gap-4">
              <a href="https://www.instagram.com/jalupro_nz" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-instagram">
                <InstagramLogo size={20} weight="light" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-facebook">
                <FacebookLogo size={20} weight="light" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-linkedin">
                <LinkedinLogo size={20} weight="light" />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold text-sm tracking-wider uppercase mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-white/70 hover:text-white text-sm transition-colors">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-semibold text-sm tracking-wider uppercase mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-white/70 hover:text-white text-sm transition-colors">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* SEO Footer Text */}
        <div className="mb-8 pb-8 border-b border-white/10">
          <p className="text-white/60 text-sm leading-relaxed max-w-4xl">
            <strong className="text-white/80">Jalupro</strong> is a leading skin booster treatment in New Zealand, 
            combining amino acids and hyaluronic acid to stimulate collagen production and improve skin quality. 
            Widely used in aesthetic clinics across Auckland, Wellington, and Christchurch, Jalupro offers natural 
            skin rejuvenation, hydration, and anti-aging benefits without adding volume. Our injectable skin 
            rejuvenation treatments are ideal for patients seeking collagen stimulation treatment NZ and 
            anti-aging treatments in New Zealand.
          </p>
        </div>

        {/* Bottom */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            &copy; {currentYear} Jalupro New Zealand. All rights reserved.
          </p>
          <p className="text-white/50 text-xs text-center md:text-right">
            For medical professionals only. Treatments must be performed by qualified practitioners.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Product Detail Page Component
const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = productData[slug];
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const handleMove = (clientX) => {
    if (!containerRef.current || !isDragging.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.min(Math.max((x / rect.width) * 100, 0), 100);
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleMouseMove = (e) => handleMove(e.clientX);
  const handleTouchMove = (e) => handleMove(e.touches[0].clientX);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-32 pb-20 text-center">
          <h1 className="font-heading text-4xl font-light text-slate-900 mb-4">Product Not Found</h1>
          <p className="text-text-muted mb-8">The product you're looking for doesn't exist.</p>
          <Link to="/#products" className="btn-primary rounded-sm inline-flex items-center gap-2">
            <ArrowLeft size={18} /> Back to Products
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="product-detail-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <button 
              onClick={() => navigate('/#products')}
              className="flex items-center gap-2 text-text-muted hover:text-primary transition-colors mb-8"
              data-testid="back-to-products"
            >
              <ArrowLeft size={18} /> Back to Products
            </button>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Product Image */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute top-4 left-4 bg-primary text-white px-4 py-2 text-sm font-medium">
                  {slug === 'jalupro-biofiller' ? 'New Generation Cross-Linked HA with Amino Acids' : slug === 'jalupro-glow-peel' ? 'Advanced Chemical Peel' : slug === 'jalupro-super-hydro' ? 'Ligaments Support · Deep Structure' : slug === 'jalupro-young-eye' ? 'Periorbital Rejuvenation' : 'Injectable Biorevitalization'}
                </div>
              </div>
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-text-muted mb-4">
                Jalupro New Zealand
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 mb-4">
                {product.name}
              </h1>
              <p className="text-xl text-primary font-light mb-6">{product.tagline}</p>
              <p className="text-text-muted leading-relaxed mb-8">{product.description}</p>

              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-accent-grey p-4 rounded-lg">
                  <p className="text-xs font-semibold tracking-wider uppercase text-text-muted mb-1">Sessions</p>
                  <p className="text-sm text-slate-800">{product.sessions}</p>
                </div>
                <div className="bg-accent-grey p-4 rounded-lg">
                  <p className="text-xs font-semibold tracking-wider uppercase text-text-muted mb-1">Results</p>
                  <p className="text-sm text-slate-800">{product.results}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/#contact"
                  className="btn-primary rounded-sm inline-flex items-center gap-2"
                  data-testid="product-enquiry-btn"
                >
                  Enquire About This Treatment <ArrowRight size={18} />
                </Link>
                
                {/* Download Brochure Button */}
                {product.brochureUrl && (
                  <a
                    href={product.brochureUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="btn-secondary rounded-sm inline-flex items-center gap-2"
                    data-testid="product-download-brochure"
                  >
                    <FileArrowDown size={18} /> Download Brochure
                  </a>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Detailed Description */}
      <section className="py-20 bg-accent-grey">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl font-light text-slate-900 mb-8">About {product.name}</h2>
            <div className="prose prose-lg text-text-muted">
              {product.longDescription.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-6 leading-relaxed">{paragraph}</p>
              ))}
            </div>

            {/* Clinical Needs - Young Eye */}
            {product.clinicalNeeds && (
              <div className="mt-10 pt-8 border-t border-slate-200">
                <h3 className="font-heading text-2xl font-light text-slate-900 mb-6 tracking-wide uppercase">Clinical Needs</h3>
                <ul className="space-y-4">
                  {product.clinicalNeeds.map((need, i) => (
                    <li key={i} className="flex items-start gap-3 pl-4 border-l-2 border-primary/30">
                      <p className="text-text-muted leading-relaxed">{need}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Classic Actions */}
            {product.classicActions && (
              <div className="mt-10 pt-8 border-t border-slate-200">
                <h3 className="font-heading text-2xl font-light text-slate-900 mb-6">Jalupro Classic:</h3>
                <div className="space-y-3 mb-6">
                  {product.classicActions.map((action, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <ArrowRight size={16} className="text-primary mt-1 flex-shrink-0" />
                      <p className="text-text-muted leading-relaxed">{action}</p>
                    </div>
                  ))}
                </div>
                {product.classicNote && (
                  <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-5">
                    <p className="text-slate-700 italic leading-relaxed">{product.classicNote}</p>
                  </div>
                )}
              </div>
            )}

            {/* Super Hydro Indications */}
            {product.superHydroIndications && (
              <div className="mt-10 pt-8 border-t border-slate-200">
                <h3 className="font-heading text-2xl font-light text-slate-900 mb-6">Uses & Indications</h3>
                <p className="text-text-muted leading-relaxed mb-6">{product.superHydroIndications.intro}</p>
                <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-5">
                  <h4 className="font-heading text-base font-medium text-slate-900 mb-2">How long does the effect last?</h4>
                  <p className="text-slate-700 italic leading-relaxed">{product.superHydroIndications.duration}</p>
                </div>
              </div>
            )}

            {/* Composition */}
            {product.composition && (
              <div className="mt-12 pt-10 border-t border-slate-200">
                <h3 className="font-heading text-2xl font-light text-slate-900 mb-8 tracking-wide uppercase">Composition</h3>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2">{product.composition.vials.count}</p>
                    <p className="font-heading text-lg font-medium text-slate-800 mb-4">{product.composition.vials.label}</p>
                    <ul className="space-y-2">
                      {product.composition.vials.items.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-text-muted text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-2">{product.composition.ampoules.count}</p>
                    <p className="font-heading text-lg font-medium text-slate-800 mb-2">{product.composition.ampoules.label}</p>
                    <p className="text-text-muted text-sm">{product.composition.ampoules.detail}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Composition List (Super Hydro format) */}
            {product.compositionList && (
              <div className="mt-12 pt-10 border-t border-slate-200">
                <h3 className="font-heading text-2xl font-light text-slate-900 mb-8 tracking-wide uppercase">Composition</h3>
                <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
                  <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-4">{product.compositionList.format}</p>
                  <div className="space-y-5">
                    {product.compositionList.components.map((comp, i) => (
                      <div key={i} className="pl-4 border-l-2 border-primary/30">
                        <p className="font-heading text-base font-medium text-slate-800 mb-1">{comp.category}</p>
                        {comp.items && <p className="text-text-muted text-sm leading-relaxed">{comp.items}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      {product.productPackageImage && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="max-w-2xl mx-auto">
              <img
                src={product.productPackageImage}
                alt={`${product.name} product packaging`}
                className="w-full h-auto"
                data-testid="product-package-image"
              />
            </div>
          </div>
        </section>
      )}

      {/* Dual Layer Approach Section - Super Hydro */}
      {product.dualLayerContent && (
        <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {/* Header */}
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl sm:text-4xl font-light text-slate-900 mb-4">
                {product.dualLayerContent.title}
              </h2>
              <p className="text-lg text-primary font-light max-w-3xl mx-auto">
                {product.dualLayerContent.subtitle}
              </p>
            </div>

            {/* Intro and Image Grid */}
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <p className="text-text-muted leading-relaxed mb-6">
                  {product.dualLayerContent.intro}
                </p>
                <p className="text-slate-600 italic">
                  Collagen is not only a skin component—it is the link, the connection of all facial structure, from the bone to the surface, holding muscles, fats and skin together in a harmonious structure.
                </p>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-4">
                <img
                  src={product.dualLayerContent.image}
                  alt="Think Dual Layer - Skin and Deep Structure"
                  className="w-full h-auto"
                />
              </div>
            </div>

            {/* Content Sections */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {product.dualLayerContent.sections.slice(0, 2).map((section, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-slate-100">
                  <h3 className="font-heading text-xl font-medium text-slate-900 mb-4">
                    {section.title}
                  </h3>
                  <p className="text-text-muted leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>

            {/* Dual Layer Approach Highlight */}
            <div className="bg-gradient-to-r from-primary to-slate-700 rounded-2xl p-8 lg:p-12 text-white">
              <h3 className="font-heading text-2xl font-light mb-6 text-center">
                The Dual-Layer Approach
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {product.dualLayerContent.sections[2]?.layers?.map((layer, index) => (
                  <div key={index} className="bg-white/10 border border-white/20 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-lg font-semibold">{index + 1}</span>
                      </div>
                      <p className="text-white/90">{layer}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-center text-white/70 mt-8 text-sm">
                This comprehensive strategy ensures optimal results by tackling the root causes of tissue aging at every layer, offering functional and aesthetic improvements.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Ligament Aging Section - Super Hydro */}
      {product.ligamentAgingContent && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {/* Section Header */}
            <div className="text-center mb-16">
              <h2 className="font-heading text-3xl sm:text-4xl font-light text-slate-900 mb-4">
                {product.ligamentAgingContent.title}
              </h2>
              <div className="w-16 h-px bg-primary mx-auto"></div>
            </div>

            {/* Intro + Anatomical Image */}
            <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
              <div>
                <p className="text-text-muted leading-relaxed text-base mb-8">
                  {product.ligamentAgingContent.intro}
                </p>

                {/* Retaining Ligaments */}
                <div className="mb-8">
                  <h3 className="font-heading text-xl font-medium text-slate-900 mb-4">
                    {product.ligamentAgingContent.retainingLigaments.title}
                  </h3>
                  <p className="text-text-muted leading-relaxed mb-4">
                    {product.ligamentAgingContent.retainingLigaments.text}
                  </p>
                  <ul className="space-y-3 mb-4">
                    {product.ligamentAgingContent.retainingLigaments.effects.map((effect, i) => (
                      <li key={i} className="flex items-start gap-3 pl-4 border-l-2 border-primary/30">
                        <div>
                          <span className="font-medium text-slate-800">{effect.term}:</span>{' '}
                          <span className="text-text-muted">{effect.desc}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <p className="text-slate-600 italic text-sm leading-relaxed">
                    {product.ligamentAgingContent.retainingLigaments.conclusion}
                  </p>
                </div>
              </div>

              {/* Anatomical Diagram */}
              <div className="bg-slate-50 rounded-2xl shadow-lg p-4 lg:sticky lg:top-8">
                <img
                  src={product.ligamentAgingContent.image}
                  alt="Facial retaining ligaments anatomy diagram"
                  className="w-full h-auto rounded-lg"
                />
                <p className="text-xs text-slate-400 text-center mt-3 px-4">
                  Source: Cohen, A. and Burkat, C. (2019) Oculofacial, Orbital, and Lacrimal Surgery. Springer, Cham, 32. Anatomy of Facelifting, p.318.
                </p>
              </div>
            </div>

            {/* Therapeutic Implication */}
            <div className="mb-16">
              <h3 className="font-heading text-2xl font-light text-slate-900 mb-6">
                {product.ligamentAgingContent.therapeuticImplication.title}
              </h3>
              <p className="text-text-muted leading-relaxed mb-6">
                {product.ligamentAgingContent.therapeuticImplication.text}
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {product.ligamentAgingContent.therapeuticImplication.cells.map((cell, i) => (
                  <div key={i} className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-6">
                    <h4 className="font-heading text-lg font-medium text-primary mb-3">{cell.name}</h4>
                    <p className="text-sm text-text-muted leading-relaxed">{cell.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-text-muted leading-relaxed mb-4">
                {product.ligamentAgingContent.therapeuticImplication.detail}
              </p>
              <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-5">
                <p className="text-slate-700 italic leading-relaxed">
                  {product.ligamentAgingContent.therapeuticImplication.insight}
                </p>
              </div>
            </div>

            {/* Cellular Foundation - Strategies */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 lg:p-12 text-white">
              <h3 className="font-heading text-2xl font-light mb-3 text-center">
                {product.ligamentAgingContent.cellularFoundation.title}
              </h3>
              <p className="text-slate-300 text-center mb-8 max-w-2xl mx-auto">
                {product.ligamentAgingContent.cellularFoundation.text}
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {product.ligamentAgingContent.cellularFoundation.strategies.map((strategy, i) => (
                  <div key={i} className="bg-white/10 border border-white/15 rounded-xl p-6">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mb-4">
                      <span className="text-lg font-semibold">{i + 1}</span>
                    </div>
                    <h4 className="font-heading text-lg font-medium text-white mb-3">{strategy.title}</h4>
                    <p className="text-white/80 text-sm leading-relaxed">{strategy.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* LIFT Protocol Section - Super Hydro */}
      {product.liftProtocol && (
        <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl sm:text-4xl font-light text-slate-900 mb-3">
                {product.liftProtocol.title}
              </h2>
              <p className="text-lg text-primary font-light">
                {product.liftProtocol.subtitle}
              </p>
            </div>
            <div className="max-w-3xl mx-auto">
              <div className="rounded-2xl overflow-hidden shadow-xl bg-white">
                <img
                  src={product.liftProtocol.image}
                  alt="L.I.F.T. Protocol - Ligaments and Fat Pads Technique injection points"
                  className="w-full h-auto"
                  data-testid="product-lift-protocol-image"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Key Ingredients - Only for Biofiller */}
      {product.keyIngredients && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <h2 className="font-heading text-3xl font-light text-slate-900 mb-8 text-center">Key Ingredients</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {product.keyIngredients.map((ingredient, index) => (
                <div key={index} className="bg-gradient-to-br from-accent-blush to-white p-6 rounded-xl border border-slate-100">
                  <h4 className="font-heading text-lg font-medium text-primary mb-3">{ingredient.name}</h4>
                  <p className="text-sm text-slate-600">{ingredient.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Biofiller Variants - Light, Medium, Deep */}
      {product.biofillerVariants && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-16">
              <h2 className="font-heading text-3xl sm:text-4xl font-light text-slate-900 mb-4">The Biofiller Range</h2>
              <p className="text-text-muted max-w-2xl mx-auto">Three targeted formulations for every depth and indication — all with the Slow-Release Patented Amino Acid Complex</p>
              <div className="w-16 h-px bg-primary mx-auto mt-6"></div>
            </div>

            <div className="space-y-24">
              {product.biofillerVariants.map((variant, index) => (
                <div key={index} data-testid={`biofiller-variant-${index}`}>
                  {/* Variant Header */}
                  <div className="mb-10">
                    <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase mb-2">Jalupro</p>
                    <h3 className="font-heading text-3xl sm:text-4xl font-light text-slate-900 mb-2">{variant.name}</h3>
                    <p className="text-lg text-slate-500 font-light">{variant.purpose}</p>
                  </div>

                  {/* Image + Content Grid */}
                  <div className={`grid lg:grid-cols-2 gap-12 items-start ${index % 2 !== 0 ? 'lg:direction-rtl' : ''}`}>
                    {/* Image - alternates side */}
                    <div className={`${index % 2 !== 0 ? 'lg:order-2' : ''}`}>
                      {variant.image && (
                        <div className="rounded-2xl overflow-hidden shadow-xl">
                          <img src={variant.image} alt={variant.name} className="w-full h-auto" />
                        </div>
                      )}
                    </div>

                    {/* Text Content */}
                    <div className={`${index % 2 !== 0 ? 'lg:order-1' : ''}`}>
                      <p className="text-text-muted leading-relaxed mb-8">{variant.description}</p>

                      {/* Indications */}
                      <div className="mb-8">
                        <h4 className="font-heading text-lg font-medium text-slate-900 mb-4">What is it preferred for?</h4>
                        <ul className="space-y-3">
                          {variant.indications.map((item, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                              <span className="text-text-muted">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Technical Details */}
                      <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                        <h4 className="font-heading text-sm font-semibold tracking-widest text-primary uppercase">Technical Details</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">Hyaluronic Acid</p>
                            <p className="text-slate-800 font-medium">{variant.ha}</p>
                          </div>
                          <div>
                            <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">Presentation</p>
                            <p className="text-slate-800 font-medium">{variant.syringe}</p>
                          </div>
                          <div className="sm:col-span-2">
                            <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">AA Complex</p>
                            <p className="text-slate-700 text-sm">{variant.aa}</p>
                          </div>
                          <div>
                            <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">Recommended Material</p>
                            <p className="text-slate-800 font-medium">{variant.needle}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Divider between variants */}
                  {index < product.biofillerVariants.length - 1 && (
                    <div className="w-full h-px bg-slate-200 mt-24"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Key Benefits Categories - Only for Glow Peel */}
      {product.keyBenefits && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <h2 className="font-heading text-3xl font-light text-slate-900 mb-8 text-center">Treatment Benefits</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {product.keyBenefits.map((category, index) => (
                <div key={index} className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-xl border border-amber-100">
                  <h4 className="font-heading text-lg font-medium text-primary mb-4 pb-3 border-b border-amber-200">
                    {category.category}
                  </h4>
                  <ul className="space-y-2">
                    {category.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check size={14} weight="bold" className="text-amber-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* What Makes Glow Peel Unique - Active Ingredients */}
      {product.uniqueIngredients && (
        <section className="py-20 bg-gradient-to-b from-slate-800 to-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl font-light mb-4">What Makes Glow Peel Unique?</h2>
              <p className="text-slate-300 max-w-2xl mx-auto">
                Getting the best of peeling without the side effects
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {product.uniqueIngredients.map((ingredient, index) => (
                <div key={index} className="bg-white/5 border border-white/10 p-6 rounded-xl hover:bg-white/10 transition-colors">
                  <h4 className="font-heading text-lg font-medium text-amber-300 mb-3">{ingredient.name}</h4>
                  <p className="text-sm text-slate-300 leading-relaxed">{ingredient.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Multifaceted Actions */}
      {product.multifacetedActions && (
        <section className="py-20 bg-accent-grey">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl font-light text-slate-900 mb-4">Multifaceted Action of Active Ingredients</h2>
              <p className="text-text-muted">Comprehensive skin treatment addressing multiple concerns</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {product.multifacetedActions.map((action, index) => (
                <div key={index} className="bg-white px-6 py-3 rounded-full border border-slate-200 text-slate-700 shadow-sm hover:shadow-md hover:border-primary/30 transition-all">
                  <span className="flex items-center gap-2">
                    <Check size={16} weight="bold" className="text-primary" />
                    {action}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits & Ideal For */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Benefits */}
            <div className="bg-accent-blush rounded-2xl p-8 lg:p-10">
              <h3 className="font-heading text-2xl font-light text-slate-900 mb-6">Key Benefits</h3>
              <div className="space-y-4">
                {product.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} weight="bold" className="text-primary" />
                    </div>
                    <p className="text-slate-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Ideal For */}
            <div className="bg-slate-800 rounded-2xl p-8 lg:p-10 text-white">
              <h3 className="font-heading text-2xl font-light mb-6">Ideal For Patients Who</h3>
              <div className="space-y-4">
                {product.idealFor.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} weight="bold" className="text-white" />
                    </div>
                    <p className="text-slate-200">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Areas */}
      <section className="py-20 bg-accent-grey">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-heading text-3xl font-light text-slate-900 mb-8 text-center">Treatment Areas</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {product.treatmentAreas.map((area, index) => (
              <div key={index} className="bg-white px-6 py-3 rounded-full border border-slate-200 text-slate-700">
                {area}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before & After */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-light text-slate-900 mb-4">Before & After Results</h2>
            <p className="text-text-muted max-w-2xl mx-auto">
              See the natural, progressive results achievable with {product.name} treatments.
            </p>
          </div>

          {/* Gallery of Before/After Images */}
          {product.beforeAfterGallery ? (
            <div className="grid md:grid-cols-2 gap-8">
              {product.beforeAfterGallery.map((item, index) => (
                <div key={index} className="space-y-4">
                  <div className="rounded-2xl overflow-hidden shadow-xl">
                    <img
                      src={item.image}
                      alt={`${product.name} before and after - Case ${index + 1}`}
                      className="w-full h-auto"
                      data-testid={`product-before-after-gallery-${index}`}
                    />
                  </div>
                  {item.caption && (
                    <p className="text-center text-sm text-slate-600 font-medium">{item.caption}</p>
                  )}
                </div>
              ))}
            </div>
          ) : product.beforeAfterCombined ? (
            /* Combined Before/After Image (for Glow Peel, Classic) */
            <div className="max-w-4xl mx-auto">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={product.beforeAfterCombined}
                  alt={`${product.name} before and after results`}
                  className="w-full h-auto"
                  data-testid="product-before-after-combined"
                />
              </div>
              {product.beforeAfterCaption && (
                <p className="text-center text-sm text-slate-600 font-medium mt-4">{product.beforeAfterCaption}</p>
              )}
            </div>
          ) : product.beforeAfter ? (
            /* Interactive Slider for other products */
            <div 
              ref={containerRef}
              className="relative max-w-3xl mx-auto aspect-[3/2] rounded-2xl overflow-hidden cursor-ew-resize select-none shadow-xl"
              onMouseDown={handleMouseDown}
              onTouchStart={handleMouseDown}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleMouseUp}
              data-testid="product-before-after-slider"
            >
              {/* After Image */}
              <img
                src={product.beforeAfter.after}
                alt="After treatment"
                className="absolute inset-0 w-full h-full object-cover"
              />
              
              {/* Before Image (Clipped) */}
              <div 
                className="absolute inset-0 overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
              >
                <img
                  src={product.beforeAfter.before}
                  alt="Before treatment"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              
              {/* Slider Handle */}
              <div 
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
                style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
                  <div className="flex gap-0.5">
                    <div className="w-0.5 h-4 bg-primary rounded-full"></div>
                    <div className="w-0.5 h-4 bg-primary rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Labels */}
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-3 py-1.5 text-sm font-medium text-primary">
                Before
              </div>
              <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur px-3 py-1.5 text-sm font-medium text-primary">
                After
              </div>
            </div>
          ) : null}

          <p className="text-center text-sm text-text-muted italic mt-6">
            *Individual results may vary. Images shown are for illustrative purposes.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-light mb-6">
            Ready to Experience {product.name}?
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-8">
            Contact us to learn more about {product.name} treatments or to find an aesthetic clinic near you in New Zealand.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/#contact"
              className="bg-white text-primary px-8 py-3 font-medium hover:bg-slate-100 transition-colors inline-flex items-center gap-2"
            >
              Contact Us <ArrowRight size={18} />
            </Link>
            <Link
              to="/#clinics"
              className="border border-white/30 text-white px-8 py-3 font-medium hover:bg-white/10 transition-colors"
            >
              Find a Clinic
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
};

// Main Home Component
const Home = () => {
  return (
    <div className="min-h-screen">
      <Navigation />
      <main>
        <HeroSection />
        <AboutSection />
        <BiohackingSection />
        <CollagenSection />
        <CollagenDeepSection />
        <ProductsSection />
        <HowItWorksSection />
        <BenefitsSection />
        <ClinicsSection />
        <ContactSection />
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products/:slug" element={<ProductDetailPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
